import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { readContract } from '@wagmi/core';

import { Address } from 'viem';

import { wagmiConfig } from '@shared/common/providers/Web3Provider/wagmi';
import { DEFAULT_TARGET_CHAIN, tokensInfo } from '@shared/constants';
import { concatCurrencyToAmount, weiToNumber } from '@shared/helpers/web3/formatters';

import { formatReward } from '@ui/uikit/utils';

import { RAFFLE_CONTRACT } from '../contract';
import { getQueryKey } from './config';
import { AmountInfo } from './types';

export type RaffleRoundTickets = {
  roundId: bigint;
  boughtTicketsCount: bigint;
  prizeInSqr: AmountInfo;
  prizeInUsdt: AmountInfo;
  allTickets: readonly bigint[];
  wonTicket: bigint | null;
  nonWonTickets: bigint[];
};

export type RaffleRoundTicketsResponse = RaffleRoundTickets | null;

type Props = {
  roundId?: bigint;
  walletAddress?: Address;
};

const getRoundTickets = async ({
  roundId,
  walletAddress,
}: Props): Promise<RaffleRoundTicketsResponse> => {
  if (!roundId || !walletAddress) {
    return null;
  }

  const response = await readContract(wagmiConfig, {
    ...RAFFLE_CONTRACT,
    chainId: DEFAULT_TARGET_CHAIN.id,
    functionName: 'getAddressInfoId',
    args: [walletAddress, roundId],
  });

  const priceSqrInUsdt = await readContract(wagmiConfig, {
    ...RAFFLE_CONTRACT,
    chainId: DEFAULT_TARGET_CHAIN.id,
    functionName: 'getPriceSQRinUSDT',
  });

  const boughtTicketsCount = response.boughtTickets;
  const wonTicket = response.wonTickets.length > 0 ? response.wonTickets[0] : null;

  const sqrToken = tokensInfo.SQR;
  const usdtToken = tokensInfo.USDT;

  const prizeInSqr = response.gotPrize;
  const prizeInUSDT = (prizeInSqr * priceSqrInUsdt) / BigInt(10 ** sqrToken.decimals);

  const prizeSqrNumber = weiToNumber(prizeInSqr, sqrToken.decimals);
  const prizeUsdtNumber = weiToNumber(prizeInUSDT, usdtToken.decimals);

  return {
    roundId,
    boughtTicketsCount,
    prizeInSqr: {
      wei: prizeInSqr,
      value: prizeSqrNumber,
      formatted: concatCurrencyToAmount(formatReward(prizeSqrNumber), sqrToken.symbol),
    },
    prizeInUsdt: {
      wei: priceSqrInUsdt,
      value: prizeUsdtNumber,
      formatted: concatCurrencyToAmount(formatReward(prizeUsdtNumber), usdtToken.symbol),
    },
    allTickets: response.ticketsInRound,
    wonTicket,
    nonWonTickets: response.ticketsInRound.filter((t) => t !== wonTicket),
  };
};

export const useRoundTickets = (
  roundId?: bigint,
  walletAddress?: Address,
  options?: UseQueryOptions<RaffleRoundTicketsResponse>,
) => {
  return useQuery({
    queryKey: getQueryKey('roundTickets', String(roundId), walletAddress),
    queryFn: () => getRoundTickets({ roundId, walletAddress }),
    enabled: !!roundId && !!walletAddress,
    ...options,
  });
};
